import { useState, useEffect } from 'react';
import { ThemeProvider } from './components/ThemeProvider';
import { FaviconManager } from './components/FaviconManager';
import { useBrand } from './hooks/useBrand';
import { useIpCapture } from './hooks/useIpCapture';
import { identifyKlaviyo, trackKlaviyo, subscribeKlaviyo } from './utils/klaviyo';
import { BRAND_IDS } from './config/brands';
import {
  DATASTORE_WEB_FORM_ID,
} from './config/env';
import {
  trimToUndefined,
  getUrlParam,
  normaliseDob,
  resolveMobileDetails,
  submitMotJourney,
} from './utils/submission';
import TrustpilotRating from './components/TrustpilotRating';
import MobileNumberInput from './components/MobileNumberInput';
import SmsLander from './components/SmsLander';
import SignatureLander from './components/SignatureLander';
import { resolveJourneyVariant, JOURNEY_VARIANTS } from './utils/journeyVariant';
import EmailInput from './components/EmailInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCircleExclamation, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import './App.css';

const AppForm = ({ uniqueID, clientid, fromSignature = false }) => {
  const brand = useBrand();

  // Read the existing Barclays journey parameters from the personalised URL.
  // The signature journey carries `clientid` (not `uniqueID`), so it must be
  // treated as a fresh load too, otherwise the URL params are never persisted
  // to sessionStorage and email/mobile are lost.
  const isFreshLoadJourney = fromSignature ? Boolean(clientid) : Boolean(uniqueID);

  const urlTitle = trimToUndefined(getUrlParam('title'));
  const urlFirstname = trimToUndefined(getUrlParam('firstname')) || trimToUndefined(getUrlParam('f')) || trimToUndefined(getUrlParam('firstName')) || trimToUndefined(getUrlParam('forename'));
  const urlLastname = trimToUndefined(getUrlParam('lastname')) || trimToUndefined(getUrlParam('l')) || trimToUndefined(getUrlParam('surname')) || trimToUndefined(getUrlParam('lastName'));
  const urlEmail = trimToUndefined(getUrlParam('email'));
  const urlMobileNumber = trimToUndefined(getUrlParam('mobileNumber')) || trimToUndefined(getUrlParam('mobile'));
  const urlRawDob = trimToUndefined(getUrlParam('rawDob')) || trimToUndefined(getUrlParam('dob'));
  const urlLine1 = trimToUndefined(getUrlParam('currentAddressLine1')) || trimToUndefined(getUrlParam('line1'));
  const urlLine2 = trimToUndefined(getUrlParam('currentAddressLine2')) || trimToUndefined(getUrlParam('line2'));
  const urlCity = trimToUndefined(getUrlParam('currentAddressCity')) || trimToUndefined(getUrlParam('city'));
  const urlPostcode = trimToUndefined(getUrlParam('currentAddressPostcode')) || trimToUndefined(getUrlParam('postcode'));

  // Check if this is a fresh load (params in the URL, not just in props)
  const isFreshLoad = isFreshLoadJourney && Boolean(window.location.search);

  // Resolve params: use URL if available, else fall back to sessionStorage
  let resolvedParams;
  if (isFreshLoad) {
    // Fresh load: use URL params and persist to sessionStorage
    resolvedParams = {
      title: urlTitle,
      firstname: urlFirstname,
      lastname: urlLastname,
      email: urlEmail,
      mobileNumber: urlMobileNumber,
      rawDob: urlRawDob,
      line1: urlLine1,
      line2: urlLine2,
      city: urlCity,
      postcode: urlPostcode,
    };
    sessionStorage.setItem('mot_params', JSON.stringify(resolvedParams));
  } else {
    // Refresh: try to restore from sessionStorage
    const stored = sessionStorage.getItem('mot_params');
    resolvedParams = stored ? JSON.parse(stored) : {
      title: undefined,
      firstname: undefined,
      lastname: undefined,
      email: undefined,
      mobileNumber: undefined,
      rawDob: undefined,
      line1: undefined,
      line2: undefined,
      city: undefined,
      postcode: undefined,
    };
  }

  const { title, firstname, lastname, email, mobileNumber, rawDob, line1, line2, city, postcode } = resolvedParams;

  // Build address object from resolved params
  const initialAddress = (line1 || line2 || city || postcode) ? {
    line1,
    line2,
    city,
    postcode,
  } : undefined;

  // Build kountPrefill object
  const kountPrefill = {
    title,
    forename: firstname,
    surname: lastname,
    dateOfBirth: normaliseDob(rawDob),
    email,
    mobileNumber: resolveMobileDetails(mobileNumber).mobileNumber,
    currentAddress: initialAddress,
  };

  const [error, setError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [kountError, setKountError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mobileNumberInput, setMobileNumberInput] = useState(mobileNumber || '');
  const [emailInput, setEmailInput] = useState(email || '');

  const ipData = useIpCapture(uniqueID);

  // Scrub query params on mount (MOT journey only — SmsLander does its own)
  useEffect(() => {
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  // Klaviyo — identify profile + track page view (once IP and email are available)
  useEffect(() => {
    if (!ipData || !email) return;

    // Browser SDK identify — links anonymous session to profile
    if (typeof window.klaviyo !== 'undefined') {
      window.klaviyo.push([
        'identify',
        {
          email,
          phone_number: mobileNumber || undefined,
          first_name: firstname || undefined,
          last_name: lastname || undefined,
        },
      ]);
    }

    identifyKlaviyo(
      email,
      mobileNumber,
      ipData.ip,
      uniqueID,
      {
        firstName: firstname,
        lastName: lastname,
      },
    );

    trackKlaviyo('Viewed Page', {
      url: window.location.href,
      page_name: 'MOT',
      email,
    });
  }, [ipData, email, mobileNumber, firstname, lastname, uniqueID]);


  const handleCopyEmail = () => {
    navigator.clipboard.writeText(brand.supportEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };



  const submit = async () => {
    setKountError("");
    setIsSubmitting(true);

    try {
      if (!mobileNumber && !mobileNumberInput) {
        setKountError("Mobile number is required");
        setIsSubmitting(false);
        return;
      }

      if (!email && !emailInput) {
        setKountError("Email address is required");
        setIsSubmitting(false);
        return;
      }

      const updatedMobileNumber = resolveMobileDetails(mobileNumberInput || mobileNumber).mobileNumber;
      const updatedEmail = emailInput.trim() || email;
      const finalKountPrefill = {
        ...kountPrefill,
        mobileNumber: updatedMobileNumber,
        email: updatedEmail,
      };

      const { response: res, kountError: ke } = await submitMotJourney({
        uniqueID,
        clientid,
        brand,
        datastoreWebFormId: DATASTORE_WEB_FORM_ID,
        kountPrefill: finalKountPrefill,
        ipData,
        additionalPayload: fromSignature
          ? {
              clientid,
              journeyVariant: JOURNEY_VARIANTS.SIGNATURE,
              signatureSubmitted: true,
            }
          : {},
      });

      if (ke) {
        setKountError(ke);
      }

      if (res.status >= 400 || !res.data?.dataid) {
        setError(true);
        setSubmitted(false);
      } else {
        setError(false);
        setSubmitted(true);
        // Klaviyo — re-identify if details changed, then subscribe + events
        const finalEmail = finalKountPrefill.email;
        const finalMobile = finalKountPrefill.mobileNumber;

        const emailChanged = Boolean(finalEmail) && finalEmail.toLowerCase() !== (email || '').toLowerCase();
        const mobileChanged = Boolean(finalMobile) && finalMobile !== resolveMobileDetails(mobileNumber).mobileNumber;

        // Re-identify with updated details so the profile is found via external_id
        if (emailChanged || mobileChanged) {
          identifyKlaviyo(
            finalEmail,
            finalMobile,
            ipData?.ip,
            uniqueID,
            {
              firstName: finalKountPrefill.forename,
              lastName: finalKountPrefill.surname,
            },
          );
        }

        subscribeKlaviyo(
          finalEmail,
          window.location.href,
          finalMobile,
          {
            firstName: finalKountPrefill.forename,
            lastName: finalKountPrefill.surname,
          },
        );
        trackKlaviyo('Submitted', {
          url: window.location.href,
          page_name: 'MOT',
          email: finalEmail,
        });
        if (emailChanged) {
          trackKlaviyo('Email Updated', {
            url: window.location.href,
            page_name: 'MOT',
            email: finalEmail,
            previous_email: email || undefined,
          });
        }
        if (mobileChanged) {
          trackKlaviyo('Mobile Updated', {
            url: window.location.href,
            page_name: 'MOT',
            email: finalEmail,
            previous_mobile: mobileNumber || undefined,
          });
        }
      }
    } catch (err) {
      setKountError("We couldn't complete the verification step right now.");
      setError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="fdc-form-wrapp">
        <div className="fdc-stepper-wrapp">
          <div className="fdc-shadow-block">
            <div className="fdc-form">
              <div className="error-message error-container">
                <h2 className="title error-title">An Error Occurred</h2>
                <p>Please refresh and try again or contact us.</p>
                {kountError && <p className="error-message-text">{kountError}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="fdc-form-wrapp">
        <div className="fdc-stepper-wrapp">
          <div className="fdc-shadow-block">
            <div className="fdc-form text-center">
              <h2 className="title">Thank you! Your details have been confirmed.</h2>
            </div>
            <div className="fdc-form gif-input">
              <div className="row">
                <div className="col-md-12">
                  <div className="text-center pt-3 pb-3 px-3">
                    <div className="text-center mb-3">
                      <FontAwesomeIcon icon={faEnvelope} className="submitted-envelope-icon" />
                    </div>
                    <p className="submitted-paragraph-small">
                      The {brand.displayName} team is reviewing your confirmed details.{' '}
                      <b>Next steps will be shared with you soon, we'll keep you informed.</b>
                    </p>
                    <p className="submitted-paragraph-medium">
                      We've sent you a confirmation email, if you haven't received it please check your 'Junk' or 'Spam' folders.
                    </p>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="text-center pt-3 pb-3 px-3">
                    <p className="submitted-inbox-attention">
                      <FontAwesomeIcon icon={faCircleExclamation} className="submitted-attention-icon" />
                      <strong>Keep an eye on your inbox</strong>
                    </p>
                    <p className="submitted-paragraph-large">
                      Your claim updates will arrive from:
                    </p>
                    <div className="submitted-flex-center">
                      <div className="support-email-box">
                        {brand.supportEmail}
                        <button
                          onClick={handleCopyEmail}
                          className="copy-email-button"
                          title="Copy email to clipboard"
                        >
                          <FontAwesomeIcon
                            icon={faCopy}
                            className={`copy-email-icon ${copied ? 'copied' : ''}`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fdc-form-wrapp">
      <div className="fdc-stepper-wrapp">
        <div
          className={`fdc-shadow-block ${isSubmitting ? "active-spinner" : ""}`}
        >
          <div className="loading-overlay"></div>
          <div className="loader"></div>

          <div className="fdc-form gif-input">
            <div className="row">
              <div className="col-md-12 text-center">
                <h2 className="title mb-4">Good news{firstname ? ` ${firstname}!` : '!'}</h2>
              </div>

              {/* Consolidated contact details messaging */}
              <div className="col-md-12">
                <div className="terms-scroll-container-inner mobile-messaging-section">
                  <p className="mb-3">
                    {fromSignature
                      ? 'Thank you for providing your signature. Please confirm your contact details below so we can keep you updated about your claim.'
                      : `Thank you for choosing ${brand.displayName} to handle your Car Finance Mis-Selling claim.`}
                  </p>

                  {/* Combo 1: has email + has mobile */}
                  {email && mobileNumber && (
                    <p className="mb-3">
                      We are progressing your claim and currently have the following mobile number and email address on file for you. We use these to send you important SMS and email updates and to contact you quickly if we need any additional information. If either is incorrect, please update them below.
                    </p>
                  )}

                  {/* Combo 2: has email + no mobile */}
                  {email && !mobileNumber && (
                    <p className="mb-3">
                      We are progressing your claim and currently have the following email address on file for you. To also ensure you receive important updates by SMS, please provide your mobile number below. If your email address is incorrect, you can update it too.
                    </p>
                  )}

                  {/* Combo 3: no email + has mobile */}
                  {!email && mobileNumber && (
                    <p className="mb-3">
                      We are progressing your claim and currently have the following mobile number on file for you. To also ensure you receive important updates by email, please provide your email address below. If your mobile number is incorrect, you can update it too.
                    </p>
                  )}

                  {/* Combo 4: no email + no mobile */}
                  {!email && !mobileNumber && (
                    <p className="mb-3">
                      We are progressing your claim, but to ensure you receive important SMS and email updates and to allow us to contact you quickly if we need any additional information, please provide your mobile number and email address below.
                    </p>
                  )}

                  <div className="mobile-input-wrapper mb-4">
                    <MobileNumberInput
                      value={mobileNumberInput}
                      onChange={setMobileNumberInput}
                      showOptionalLabel={false}
                    />
                  </div>

                  <div className="mobile-input-wrapper mb-4">
                    <EmailInput
                      value={emailInput}
                      onChange={setEmailInput}
                      showOptionalLabel={false}
                    />
                  </div>

                  <p className="mb-0">
                    Thank you for your continued trust. We're committed to achieving the best possible outcome for you.
                  </p>
                </div>
              </div>

              {kountError && (
                <div className="col-md-12 text-center mb-3">
                  <p className="error-message-text">{kountError}</p>
                </div>
              )}

              {/* Submit button */}
              <div className="col-md-12 text-center">
                <div className="row gx-2 steps action-button mt-4">
                  <div className="col-12 text-center">
                    <button
                      type="button"
                      className="btn-w color3 next-button"
                      onClick={submit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Confirming…' : 'Confirm & Continue'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const ErrorScreen = ({ brand }) => {
  return (
    <div className="fdc-form-wrapp">
      <div className="fdc-stepper-wrapp">
        <div className="fdc-shadow-block">
          <div className="fdc-form">
            <h2
              className="title text-center"
              style={{ color: 'var(--brand-primary)', marginBottom: 0 }}
            >
              Unfortunately we've encountered a problem.
            </h2>
            <div className="error-screen-message">
              We're sorry, please contact our Customer Services on{" "}
              <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AppContent = ({ fromSignature = false } = {}) => {
  const brand = useBrand();
  let uniqueID = trimToUndefined(getUrlParam('uniqueID'))?.replace(/^=+/, '');
  let clientid = trimToUndefined(getUrlParam('clientid'));

  // Persist uniqueID and clientid to sessionStorage for survival across refresh
  if (uniqueID) {
    sessionStorage.setItem('mot_uniqueID', uniqueID);
  } else {
    uniqueID = sessionStorage.getItem('mot_uniqueID') || undefined;
  }

  if (clientid) {
    sessionStorage.setItem('mot_clientid', clientid);
  } else {
    clientid = sessionStorage.getItem('mot_clientid') || undefined;
  }

  // The signature journey flows straight into the contact-confirmation form. The
  // default MOT form still requires a uniqueID.
  if (!uniqueID && !fromSignature) {
    return <ErrorScreen brand={brand} />;
  }

  return (
    <AppForm
      uniqueID={uniqueID}
      clientid={clientid}
      fromSignature={fromSignature}
    />
  );
};

const SignatureJourney = () => {
  const uniqueID = trimToUndefined(getUrlParam('uniqueID'))?.replace(/^=+/, '');
  const [signatureResult, setSignatureResult] = useState(null);

  // Start the existing Barclays IP/datastore session as soon as this journey opens.
  useIpCapture(uniqueID);

  if (signatureResult) {
    return <AppContent fromSignature />;
  }

  return <SignatureLander onSubmitted={setSignatureResult} />;
};

function BrandHeader() {
  const brand = useBrand();
  const logoClass = brand.id === BRAND_IDS.MLM ? 'logo-text logo-mlm' : 'logo-text';
  const showMlmLogo = brand.id === BRAND_IDS.CLAIM1 || brand.id === BRAND_IDS.YCM;

  return (
    <div className="header-wrapper header-wrapper-no-padding">
      <div className="container">
        <div className="row g-0">
          <div className="site-logo d-flex flex-column align-items-center gap-3">
            <div className="logo-container">
              <div className="primary-logo-wrapper">
                <img
                  src={brand.logo}
                  alt={`${brand.displayName} logo`}
                  className={logoClass}
                />
                <TrustpilotRating />
              </div>
              {showMlmLogo && (
                <img
                  src="/logos/MLM Logo without tagline.svg"
                  alt="MLM logo"
                  className="logo-text logo-secondary"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const variant = resolveJourneyVariant(
    window.location.search,
    window.sessionStorage,
  );

  return (
    <ThemeProvider>
      <FaviconManager />
      {variant === JOURNEY_VARIANTS.DUAL_REP ? (
        <SmsLander />
      ) : variant === JOURNEY_VARIANTS.SIGNATURE ? (
        <>
          <BrandHeader />
          <div className="main">
            <div className="container">
              <SignatureJourney />
            </div>
          </div>
        </>
      ) : (
        <>
          <BrandHeader />
          <div className="main">
            <div className="container">
              <AppContent />
            </div>
          </div>
        </>
      )}
    </ThemeProvider>
  );
}
